/**
 * Coalesces concurrent fetch() calls to Shopify's cart add endpoint when payloads
 * target the same variant + quantity + selling plan (Recharge + theme both POST).
 *
 * Important: returning one shared Response breaks multiple consumers — the body stream
 * can only be read once ("Failed to execute 'json' on Response: body stream already read").
 * We read the body once into text, then each caller gets a fresh Response built from that text.
 *
 * Loaded early on product templates only — see snippets/scripts.liquid.
 */
(function installRaindropCartAddDedupe() {
  if (typeof window === "undefined" || window.__raindropCartAddDedupeInstalled) return;
  window.__raindropCartAddDedupeInstalled = true;

  const origFetch = window.fetch.bind(window);

  /** @typedef {{ ok: boolean; status: number; statusText: string; headers: Headers; bodyText: string }} CachedCartAdd */

  /** @type {Map<string, Promise<CachedCartAdd>>} */
  const inflight = new Map();

  /**
   * Stable key for subscription / one-time PDP adds so FormData vs JSON bodies dedupe together.
   * @param {string} url
   * @param {RequestInit | undefined} init
   * @returns {string | null}
   */
  function dedupeKey(url, init) {
    if (!url || url.indexOf("/cart/add") === -1) return null;

    const body = init?.body;
    if (body instanceof FormData) {
      const id = body.get("id") ?? body.get("items[0][id]");
      if (!id) return null;
      const qty = body.get("quantity") ?? body.get("items[0][quantity]") ?? "1";
      const plan = body.get("selling_plan") ?? body.get("items[0][selling_plan]") ?? "";
      return `raindrop-cart-add|${id}|${qty}|${plan}`;
    }

    if (body && typeof body === "string") {
      try {
        const parsed = JSON.parse(body);
        const items = parsed.items;
        if (Array.isArray(items) && items[0]) {
          const it = items[0];
          const id = it.id;
          if (!id) return null;
          const qty = it.quantity ?? 1;
          const plan = it.selling_plan ?? "";
          return `raindrop-cart-add|${id}|${qty}|${plan}`;
        }
      } catch (_) {
        return null;
      }
    }

    return null;
  }

  /**
   * @param {CachedCartAdd} cached
   * @returns {Response}
   */
  function responseFromCache(cached) {
    return new Response(cached.bodyText, {
      status: cached.status,
      statusText: cached.statusText,
      headers: new Headers(cached.headers),
    });
  }

  window.fetch = function (input, init) {
    const url = typeof input === "string" ? input : input instanceof Request ? input.url : String(input?.url ?? "");
    const nextInit = init ?? {};

    const key = dedupeKey(url, nextInit);
    if (!key) {
      return origFetch(input, init);
    }

    let cachePromise = inflight.get(key);
    if (!cachePromise) {
      cachePromise = origFetch(input, init)
        .then(async (response) => ({
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          bodyText: await response.text(),
        }))
        .finally(() => {
          setTimeout(() => {
            const cur = inflight.get(key);
            if (cur === cachePromise) inflight.delete(key);
          }, 400);
        });

      inflight.set(key, cachePromise);
    }

    return cachePromise.then(responseFromCache);
  };
})();
